/**
 * @typedef {Object} EatAndPlayMenuItem
 * @property {string} itemKey
 * @property {string} title
 * @property {string} [description]
 * @property {string} [price]
 * @property {boolean} [starred]
 * @property {string} [groupKey]
 */

/**
 * @typedef {Object} EatAndPlayMenuSection
 * @property {string} sectionKey
 * @property {string} title
 * @property {string} [description]
 * @property {string} [image]
 * @property {string} [sideGroup]
 * @property {Record<string, EatAndPlayMenuItem>} items
 */

/**
 * @typedef {Object} EatAndPlayExtraGroup
 * @property {string} groupKey
 * @property {string} title
 */

/**
 * @typedef {Object} EatAndPlayExtra
 * @property {string} extraKey
 * @property {string} title
 * @property {string} [description]
 * @property {boolean} [hideItemsOnCash]
 * @property {boolean} [hideOnTournament]
 * @property {boolean} [starred]
 * @property {EatAndPlayExtraGroup[]} groups
 * @property {Record<string, EatAndPlayMenuItem>} items
 */

/**
 * @typedef {Object} EatAndPlayMenu
 * @property {boolean} fromPayload
 * @property {string|number} [id]
 * @property {string} [key]
 * @property {string} heading
 * @property {string} [description]
 * @property {string} [availableOnTablesLabel]
 * @property {boolean} showPrices
 * @property {string[]} sectionOrder
 * @property {Record<string, EatAndPlayMenuSection>} sections
 * @property {Record<string, EatAndPlayExtra>} extras
 */

const asRecord = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

const asArray = (value) => (Array.isArray(value) ? value : []);

const text = (value) => (typeof value === "string" ? value.trim() : "");

const mapItem = (item) => {
  const row = asRecord(item);
  if (!row) return null;
  const itemKey = text(row.itemKey);
  const title = text(row.title);
  if (!itemKey || !title) return null;
  return {
    itemKey,
    title,
    description: text(row.description) || undefined,
    price: text(row.price) || undefined,
    starred: Boolean(row.starred),
    groupKey: text(row.groupKey) || undefined,
  };
};

const itemsByKey = (items) => {
  /** @type {Record<string, EatAndPlayMenuItem>} */
  const mapped = {};
  for (const item of asArray(items)) {
    const next = mapItem(item);
    if (next) mapped[next.itemKey] = next;
  }
  return mapped;
};

/**
 * Maps a Payload `eat-and-play-menus` document to the shape TabContent / MenuCategory consume.
 *
 * @param {Record<string, unknown>} doc
 * @param {string} [_locale]
 * @returns {EatAndPlayMenu | null}
 */
export function mapEatAndPlayMenu(doc, _locale) {
  const row = asRecord(doc);
  if (!row) return null;

  const heading = text(row.heading);
  if (!heading && !text(row.key)) return null;

  /** @type {Record<string, EatAndPlayMenuSection>} */
  const sections = {};
  const sectionOrder = [];

  for (const category of asArray(row.categories)) {
    const section = asRecord(category);
    if (!section) continue;
    const sectionKey = text(section.sectionKey);
    const title = text(section.title);
    if (!sectionKey || !title) continue;
    sectionOrder.push(sectionKey);
    sections[sectionKey] = {
      sectionKey,
      title,
      description: text(section.description) || undefined,
      image: text(section.image) || undefined,
      sideGroup: text(section.sideGroup) || "none",
      items: itemsByKey(section.items),
    };
  }

  /** @type {Record<string, EatAndPlayExtra>} */
  const extras = {};
  for (const extra of asArray(row.extras)) {
    const group = asRecord(extra);
    if (!group) continue;
    const extraKey = text(group.extraKey);
    const title = text(group.title);
    if (!extraKey || !title) continue;
    extras[extraKey] = {
      extraKey,
      title,
      description: text(group.description) || undefined,
      hideItemsOnCash: Boolean(group.hideItemsOnCash),
      hideOnTournament: Boolean(group.hideOnTournament),
      starred: Boolean(group.starred),
      groups: asArray(group.groups)
        .map((entry) => {
          const g = asRecord(entry);
          if (!g) return null;
          const groupKey = text(g.groupKey);
          const groupTitle = text(g.title);
          if (!groupKey || !groupTitle) return null;
          return { groupKey, title: groupTitle };
        })
        .filter(Boolean),
      items: itemsByKey(group.items),
    };
  }

  return {
    fromPayload: true,
    id: row.id != null ? row.id : undefined,
    key: text(row.key) || undefined,
    heading: heading || text(row.key),
    description: text(row.description) || undefined,
    availableOnTablesLabel: text(row.availableOnTablesLabel) || undefined,
    showPrices: Boolean(row.showPrices),
    sectionOrder,
    sections,
    extras,
  };
}

/**
 * @param {{ docs?: unknown[] }} payloadResponse
 * @param {string} [locale]
 * @returns {EatAndPlayMenu | null}
 */
export function mapEatAndPlayMenuResponse(payloadResponse, locale) {
  const docs = Array.isArray(payloadResponse?.docs) ? payloadResponse.docs : [];
  const first = docs[0];
  return first ? mapEatAndPlayMenu(first, locale) : null;
}
